import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { UserAccountNav } from '@/components/layouts/nav/user-account-nav'

import { useAuth } from '@/contexts/auth-context'

const NAV_ITEMS = [
  { label: 'Learning path', href: '/learning-path' },
  { label: 'Blog', href: '/blogs' },
  { label: 'Webinars', href: '/webinars' },
  { label: 'Forum', href: '/forum', disabled: true },
  { label: 'Hall of fame', href: '/hall-of-fame' },
]

interface GeneralV1OverlayHeaderProps {
  lang?: string
  className?: string
}

export default function GeneralV1OverlayHeader({
  lang = 'ENG',
  className,
}: GeneralV1OverlayHeaderProps) {
  const [scrolled, setScrolled] = React.useState(false)
  const [currentLang, setCurrentLang] = React.useState(lang)
  const [languageOpen, setLanguageOpen] = React.useState(false)
  const { isLoading, user } = useAuth()

  React.useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <header
      data-testid="gv1-overlay-header"
      className={cn(
        'absolute top-0 left-0 w-full z-50 transition-colors duration-300',
        scrolled ? 'bg-[var(--gv1-bg-primary)]/80 backdrop-blur-xl' : 'bg-transparent',
        className
      )}
      style={{
        height: 'var(--gv1-header-height, 96px)',
        paddingLeft: 'var(--gv1-header-px, 104px)',
        paddingRight: 'var(--gv1-header-px, 104px)',
      }}
    >
      <div className="flex items-center justify-between h-full relative">
        {/* Left: Logo */}
        <Link href="/" className="flex-shrink-0 flex items-center z-10">
          <Image
            src="/templates/general-v1/assets/edunodes-logo.svg"
            alt="Edunodes"
            width={160}
            height={38}
            className="object-contain"
          />
        </Link>

        {/* Center: Nav Pill (absolute for true centering) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 hidden lg:flex">
          <nav className="flex items-center rounded-full border border-white/15 bg-white/15 px-3 py-1.5 backdrop-blur-xl">
            {NAV_ITEMS.map((item) => item.disabled ? (
              <span
                key={item.href}
                aria-disabled="true"
                className="cursor-not-allowed whitespace-nowrap px-4 py-2 text-sm font-medium text-white/35"
              >
                {item.label}
              </span>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className="whitespace-nowrap px-4 py-2 text-sm font-medium text-white/80 transition-colors hover:text-white"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Right: Actions */}
        <div className="relative z-10 flex items-center gap-3">
          {!isLoading && (
            user ? (
              <UserAccountNav
                user={user}
                triggerClassName="bg-white/10 hover:bg-white/20 focus-visible:ring-white/70"
                avatarClassName="size-10 ring-white/40"
              />
            ) : (
              <Link
                href="/sign-in"
                className="rounded-full border border-white/30 px-5 py-2 text-sm font-medium text-white hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                Sign In
              </Link>
            )
          )}
          <button
            type="button"
            aria-label="Change language"
            aria-expanded={languageOpen}
            onClick={() => setLanguageOpen(open => !open)}
            className="flex size-10 items-center justify-center rounded-full bg-white/85 text-slate-700 transition-colors hover:bg-white"
          >
            <GlobeIcon className="size-5" />
          </button>

          {languageOpen && <div className="absolute right-0 top-[54px] w-[132px] overflow-hidden rounded-2xl border border-white/15 bg-white/20 p-1.5 backdrop-blur-xl">
            {['ENG', 'VIE'].map((language) => (
              <button
                key={language}
                type="button"
                onClick={() => {
                  setCurrentLang(language)
                  setLanguageOpen(false)
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-white transition-colors',
                  currentLang === language ? 'bg-white/15' : 'hover:bg-white/10'
                )}
              >
                <LanguageMark language={language} />
                <span>{language}</span>
                {currentLang === language && <CheckIcon className="ml-auto size-4" />}
              </button>
            ))}
          </div>}
        </div>
      </div>
    </header>
  )
}

function LanguageMark({ language }: { language: string }) {
  return (
    <span
      className={cn(
        'size-4 rounded-full border border-white/50',
        language === 'ENG' ? 'bg-gradient-to-br from-blue-600 via-white to-red-500' : 'bg-red-600'
      )}
      aria-hidden="true"
    />
  )
}

function GlobeIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      <path d="M2 12h20" />
    </svg>
  )
}

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}
